/**
 * Unfinished-brief draft — everything the "Pick up where you left off" prompt needs to
 * offer a resume, and everything the Order page needs to restore one.
 *
 * Purely local: there is no backend for this. The whole brief is kept in
 * localStorage so it survives a closed tab, because a commission is a
 * considered purchase that people walk away from and come back to.
 *
 * Follows the same shape as the app's other persistence helpers (see
 * `utils/postAuthRedirect.ts`): one KEY constant, named read/write functions,
 * every access guarded so private mode degrades quietly.
 */

const DRAFT_KEY = 'briefDraft';
/** Dismissal is per-tab, so it can never strand someone for a day. */
const PROMPT_KEY = 'briefPromptDismissed';
/** An earlier build snoozed for 24h in localStorage; clear it on sight. */
const LEGACY_PROMPT_KEY = 'briefPromptSnooze';

/** A draft older than this is stale — the occasion has probably passed. */
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export const PROMPT = {
  /** Shown as soon as a brief is in progress. */
  firstDelayMs: 0,
  /** How long the full card stays up; the progress bar tracks exactly this. */
  visibleMs: 60_000,
  /**
   * Gap before the pill expands back into the full card. The pill itself never
   * goes away while the brief is unfinished, so this is only about how often
   * the larger reminder returns — the buyer can resume at any moment.
   */
  repeatEveryMs: 120_000,
} as const;

/**
 * New on every page load (a module variable resets on reload, unlike
 * sessionStorage). Blob URLs from `URL.createObjectURL` die with the page, so
 * this tells us whether the ones in a draft are still resolvable.
 */
const PAGE_SESSION = Math.random().toString(36).slice(2);

/**
 * One style reference in a brief, stored by what it *is* rather than by a URL.
 *
 * A piece from our own archive is stored as its post id: that identity cannot
 * rot the way a storage URL can, and it re-resolves to a fresh URL on restore.
 * The `url` alongside it is only a cache so the form can paint instantly.
 *
 * A device upload has no durable form until the brief is submitted, so it
 * survives within the page-load that created it and otherwise comes back as a
 * named placeholder rather than vanishing.
 */
export interface StyleRef {
  kind: 'post' | 'url' | 'upload';
  /** archive_posts.id — the durable identity, for `post` refs. */
  postId?: string;
  /** Last known image URL. For a `post` this is a re-resolvable cache. */
  url?: string;
  /** File name, for an `upload` that could not be restored. */
  name?: string;
}

/** The Order page's form state. Mirrors its `Brief` interface. */
export interface BriefFields {
  occasion: string;
  eventDate: string;
  note: string;
  styleImages: string[];
  styleLinks: string[];
  fabricChoice: '' | 'have' | 'source';
  fullName: string;
  email: string;
  phone: string;
  phoneIsWhatsapp: boolean;
  location: string;
}

export interface BriefDraft {
  savedAt: number;
  pageSession: string;
  /** Zero-based index of the step they stopped on. */
  stepIdx: number;
  totalSteps: number;
  /** The piece they were briefing, e.g. "Scarlet fringe corset". */
  title: string;
  imageUrl: string | null;
  /** The full form state, restored verbatim when they come back. */
  fields: BriefFields;
  /** Style references, classified so each can be restored on its own terms. */
  refs: StyleRef[];
}

const read = <T>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const write = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode or full quota — the draft is an enhancement, never required */
  }
};

const isBlobUrl = (url: string) => url.startsWith('blob:');

export const saveBriefDraft = (
  draft: Omit<BriefDraft, 'savedAt' | 'pageSession'>,
): void => write(DRAFT_KEY, { ...draft, savedAt: Date.now(), pageSession: PAGE_SESSION });

/**
 * The current draft, or null when there is none, it is stale, or there is
 * nothing worth resuming.
 *
 * Blob URLs for photos picked from the device only resolve while the page that
 * created them is alive, so after a reload they are dropped rather than
 * restored as broken thumbnails. Hosted references (the dress they came from,
 * pasted links) always survive.
 */
export const loadBriefDraft = (): BriefDraft | null => {
  const draft = read<BriefDraft>(DRAFT_KEY);
  if (!draft) return null;

  if (Date.now() - draft.savedAt > DRAFT_TTL_MS) {
    clearBriefDraft();
    return null;
  }
  // Nothing to resume on the first step — they had not invested anything yet.
  if (draft.stepIdx <= 0) return null;

  // Device uploads only resolve while the page that created them is alive.
  const samePage = draft.pageSession === PAGE_SESSION;
  const refs = (draft.refs ?? []).map((ref) =>
    ref.kind === 'upload' && !samePage ? { ...ref, url: undefined } : ref,
  );

  // `styleImages` is what the form renders; drop anything with no usable URL.
  const styleImages = refs
    .map((ref) => ref.url)
    .filter((url): url is string => !!url && (samePage || !isBlobUrl(url)));

  return { ...draft, refs, fields: { ...draft.fields, styleImages } };
};

export const clearBriefDraft = (): void => {
  try {
    localStorage.removeItem(DRAFT_KEY);
    localStorage.removeItem(LEGACY_PROMPT_KEY);
    sessionStorage.removeItem(PROMPT_KEY);
  } catch {
    /* nothing to recover */
  }
};

/**
 * Dismissed with × on the pill — quiet for the rest of this tab.
 *
 * Deliberately *not* persisted across visits: an unfinished brief is worth
 * money to the buyer, and a mis-tap should never cost them the way back to it
 * for a day. Closing the tab resets it.
 */
export const snoozePrompt = (): void => {
  try {
    sessionStorage.setItem(PROMPT_KEY, '1');
  } catch {
    /* private mode — the prompt simply keeps showing */
  }
};

export const isPromptSnoozed = (): boolean => {
  try {
    // Retire the old 24-hour lock so anyone holding one is freed immediately.
    localStorage.removeItem(LEGACY_PROMPT_KEY);
    return sessionStorage.getItem(PROMPT_KEY) === '1';
  } catch {
    return false;
  }
};

/** "21 Nov" — the compact form the prompt shows. */
export const formatEventDate = (value: string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
